import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowUpRight,
  FiCheckCircle,
  FiBarChart2,
  FiGlobe,
  FiLock,
  FiTrendingUp,
  FiArrowRight,
} from "react-icons/fi";
import { LOGO_BASE64, BRAND_TEXT_BASE64 } from "../utils/brandAssets";
import { DASHBOARD_URL } from "../utils/domain";
import { featureCatalogSections } from "../utils/featureCatalog";
import "../styles/Landing.css";

export default function LandingPage({ user }) {
  useEffect(() => {
    document.title = "LinkSquire - Link Intelligence for Modern Teams";
  }, []);

  useEffect(() => {
    const cards = Array.from(document.querySelectorAll(".landing-feature-card"));

    if (!cards.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" }
    );

    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page">
      <div className="landing-bg-shape landing-bg-shape-one" aria-hidden="true" />
      <div className="landing-bg-shape landing-bg-shape-two" aria-hidden="true" />

      <nav className="landing-navbar">
        <div className="landing-navbar-left">
          <a href="/" className="landing-brand" draggable="false">
            <img
              src={LOGO_BASE64}
              alt=""
              className="landing-brand-logo"
              draggable="false"
            />
            <img
              src={BRAND_TEXT_BASE64}
              alt=""
              className="landing-brand-text"
              draggable="false"
            />
          </a>
        </div>
        <div className="landing-navbar-right">
          {user ? (
            <a href={DASHBOARD_URL} className="btn-primary landing-cta-btn">
              Go to Dashboard <FiArrowRight size={14} />
            </a>
          ) : (
            <>
              <a href={DASHBOARD_URL} className="landing-signin-btn">
                Sign In
              </a>
              <a href={DASHBOARD_URL} className="btn-primary landing-cta-btn">
                Sign Up <FiArrowRight size={14} />
              </a>
            </>
          )}
        </div>
      </nav>

      <section className="landing-hero">
        <div className="landing-hero-grid">
          <div className="landing-hero-content">
            <span className="landing-badge">
              <FiTrendingUp size={14} /> Built for growth-focused teams
            </span>
            <h1 className="landing-hero-title">
              Your links should do more than redirect.
            </h1>
            <p className="landing-hero-subtitle">
              LinkSquire gives marketing, product, and creator teams one place
              to shorten, brand, and analyze every campaign link with speed and
              clarity.
            </p>
            <div className="landing-hero-actions">
              <a href={DASHBOARD_URL} className="btn-primary landing-hero-btn">
                Launch Dashboard <FiArrowRight size={16} />
              </a>
              <a href="#features" className="btn-secondary landing-secondary-btn">
                Explore Features <FiArrowUpRight size={15} />
              </a>
            </div>
            <div className="landing-hero-points">
              <span>
                <FiCheckCircle size={14} /> Branded short domains
              </span>
              <span>
                <FiCheckCircle size={14} /> Real-time click analytics
              </span>
              <span>
                <FiCheckCircle size={14} /> QR and campaign tracking
              </span>
            </div>
          </div>

          <aside className="landing-hero-panel" aria-label="Analytics preview">
            <div className="landing-panel-top">
              <p>Campaign Snapshot</p>
              <span>Live</span>
            </div>
            <div className="landing-metric-row">
              <MetricCard value="24.8k" label="Clicks this month" trend="+18.4%" />
              <MetricCard value="62%" label="Mobile traffic" trend="+6.1%" />
            </div>
            <div className="landing-panel-list">
              <AnalyticsItem label="Top source" value="Instagram Stories" />
              <AnalyticsItem label="Top region" value="United States" />
              <AnalyticsItem label="Best device" value="iPhone / Safari" />
            </div>
            <div className="landing-panel-footer">
              <FiBarChart2 size={15} /> Clear insights. Faster decisions.
            </div>
          </aside>
        </div>
      </section>

      <section className="landing-trust">
        <p className="landing-trust-label">Trusted for high-velocity campaigns</p>
        <div className="landing-trust-grid">
          <TrustItem title="99.99%" subtitle="redirect uptime" />
          <TrustItem title="< 60 ms" subtitle="global redirect latency" />
          <TrustItem title="140+" subtitle="countries tracked" />
          <TrustItem title="24/7" subtitle="availability monitoring" />
        </div>
      </section>

      <section className="landing-comparison">
        <div className="landing-comparison-header">
          <span className="landing-feature-group-eyebrow">Why LinkSquire wins</span>
          <h2 className="landing-section-title">Built for teams that need more than simple short links</h2>
          <p className="landing-section-subtitle">
            LinkSquire combines branded links, bio pages, campaigns, analytics, experimentation, and governance in one clean platform.
          </p>
        </div>
        <div className="landing-comparison-grid">
          <ComparisonCard
            title="Unified workflow"
            desc="One place for link creation, campaign grouping, bio pages, analytics, and advanced routing."
          />
          <ComparisonCard
            title="Real measurement"
            desc="Detailed click data, conversions, geo-routing, and retargeting support make the platform decision-ready."
          />
          <ComparisonCard
            title="Better brand control"
            desc="Custom domains, OG previews, password gates, and expiration fallback keep every surface on-brand."
          />
          <ComparisonCard
            title="Scale with structure"
            desc="Bulk operations and campaign grouping reduce admin work as your link library grows."
          />
        </div>
      </section>

      <section className="landing-features" id="features">
        <h2 className="landing-section-title">A complete command center for your links</h2>
        <p className="landing-section-subtitle">
          Every capability from LinkSquire is grouped below so visitors can explore the full product surface with clear routes and dedicated pages.
        </p>

        {featureCatalogSections.map((section) => (
          <div className="landing-feature-group" key={section.title}>
            <div className="landing-feature-group-header">
              <span className="landing-feature-group-eyebrow">{section.eyebrow}</span>
              <h3>{section.title}</h3>
              <p>{section.subtitle}</p>
            </div>
            <div className="landing-features-grid">
              {section.items.map((feature, index) => (
                <FeatureCard
                  key={feature.slug}
                  feature={feature}
                  index={index}
                />
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="landing-workflow">
        <div className="landing-workflow-copy">
          <h2 className="landing-section-title">Built for modern campaign workflows</h2>
          <p className="landing-section-subtitle">
            From launch to optimization, your team can move from idea to
            measurable impact in minutes.
          </p>
          <div className="landing-workflow-points">
            <WorkflowPoint
              icon={<FiGlobe size={16} />}
              title="Launch everywhere"
              desc="Use one smart link across social, email, paid ads, and offline campaigns."
            />
            <WorkflowPoint
              icon={<FiBarChart2 size={16} />}
              title="Measure what matters"
              desc="Break down traffic by source, region, browser, and device with clean visual analytics."
            />
            <WorkflowPoint
              icon={<FiLock size={16} />}
              title="Keep control"
              desc="Manage edits, expirations, and access boundaries from a single dashboard."
            />
          </div>
        </div>
        <div className="landing-workflow-card">
          <p className="landing-workflow-card-title">Typical setup time</p>
          <h3>Under 3 minutes</h3>
          <p>
            Create your first branded short link, add tracking, and share it
            across channels without engineering handoffs.
          </p>
          <a href={DASHBOARD_URL} className="landing-inline-link">
            Start building now <FiArrowRight size={14} />
          </a>
        </div>
      </section>

      <section className="landing-how-it-works">
        <h2 className="landing-section-title">How teams use LinkSquire</h2>
        <div className="landing-steps">
          <Step
            number="1"
            title="Create and brand"
            desc="Drop in a destination URL and publish a clean short link with your preferred slug."
          />
          <Step
            number="2"
            title="Distribute confidently"
            desc="Share once across every channel, including social, ads, newsletters, and offline QR touchpoints."
          />
          <Step
            number="3"
            title="Optimize in real time"
            desc="Monitor performance and update routing instantly when campaign goals change."
          />
        </div>
      </section>

      <section className="landing-cta-section">
        <h2 className="landing-cta-title">Turn every shared URL into measurable growth</h2>
        <p className="landing-cta-subtitle">
          Move faster with a link platform designed for modern marketing and
          product teams.
        </p>
        <div className="landing-hero-actions">
          <a href={DASHBOARD_URL} className="btn-primary landing-hero-btn">
            Start Free in Dashboard <FiArrowRight size={16} />
          </a>
          {!user && (
            <a href={DASHBOARD_URL} className="btn-secondary landing-secondary-btn">
              Sign In <FiArrowUpRight size={15} />
            </a>
          )}
        </div>
      </section>

      <footer className="landing-footer">
        <p>&copy; {new Date().getFullYear()} LinkSquire. All rights reserved.</p>
      </footer>
    </div>
  );
}

function FeatureCard({ feature, index }) {
  const FeatureIcon = feature.icon;

  return (
    <Link
      to={`/features/${feature.slug}`}
      className="landing-feature-card"
      style={{ "--stagger-delay": `${index * 85}ms` }}
    >
      <div className="landing-feature-icon">
        <FeatureIcon size={22} width={22} height={22} aria-hidden="true" focusable="false" />
      </div>
      <h3 className="landing-feature-title">{feature.title}</h3>
      <p className="landing-feature-desc">{feature.description}</p>
      <span className="landing-feature-link">
        Learn more <FiArrowUpRight size={14} />
      </span>
    </Link>
  );
}

function MetricCard({ value, label, trend }) {
  return (
    <div className="landing-metric-card">
      <h4>{value}</h4>
      <p>{label}</p>
      <span>{trend}</span>
    </div>
  );
}

function AnalyticsItem({ label, value }) {
  return (
    <div className="landing-analytics-item">
      <p>{label}</p>
      <span>{value}</span>
    </div>
  );
}

function TrustItem({ title, subtitle }) {
  return (
    <div className="landing-trust-item">
      <h4>{title}</h4>
      <p>{subtitle}</p>
    </div>
  );
}

function WorkflowPoint({ icon, title, desc }) {
  return (
    <div className="landing-workflow-point">
      <div className="landing-workflow-icon">{icon}</div>
      <div>
        <h4>{title}</h4>
        <p>{desc}</p>
      </div>
    </div>
  );
}

function ComparisonCard({ title, desc }) {
  return (
    <div className="landing-comparison-card">
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}

function Step({ number, title, desc }) {
  return (
    <div className="landing-step">
      <div className="landing-step-number">{number}</div>
      <h3 className="landing-step-title">{title}</h3>
      <p className="landing-step-desc">{desc}</p>
    </div>
  );
}
